import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { authedLayoutRoute } from './authed.js';
import type { CurrencyCode } from '@kora/types';
import { listAdvances, type AdvanceListRow } from '../api/advances.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { EmployeePickerModal } from '../components/EmployeePickerModal.js';

/**
 * /loans — HR loans portfolio (PR ⑦f). An org-wide view of disbursed/active/
 * paid loans with their repayment status (paid-to-date, remaining balance,
 * progress). Complements /loans/requests (the review + disburse queue) and the
 * per-employee /employees/$id/advances detail. RBAC: owner|admin|hr.
 */
export const loansPortfolioRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/loans',
  component: LoansPortfolioPage,
});

const FILTERS = {
  live: { label: 'Live', status: 'disbursed,active' },
  paid_off: { label: 'Paid off', status: 'paid_off' },
  forgiven: { label: 'Forgiven', status: 'forgiven' },
  all: { label: 'All', status: undefined },
} as const;
type FilterKey = keyof typeof FILTERS;

function LoansPortfolioPage(): JSX.Element {
  const [filter, setFilter] = useState<FilterKey>('live');
  const [picker, setPicker] = useState<'new' | 'migration' | null>(null);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['loans-portfolio', filter],
    queryFn: () => listAdvances(FILTERS[filter].status ? { status: FILTERS[filter].status } : {}),
  });
  const rows = data?.advances ?? [];

  return (
    <section className="p-8 space-y-6 max-w-5xl">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl">Loans</h1>
          <p className="text-sm text-gray-400">All employee loans and their repayment status.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPicker('new')}
            className="rounded bg-white text-black px-3 py-1.5 text-sm font-medium hover:opacity-90"
          >
            + Create new loan
          </button>
          <button
            type="button"
            onClick={() => setPicker('migration')}
            className="rounded border border-gray-700 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-900"
          >
            + Migrate existing loan
          </button>
          <Link to="/loans/requests" className="text-xs text-gray-400 hover:underline whitespace-nowrap">
            Requests &amp; disbursement →
          </Link>
        </div>
      </header>

      {picker ? <EmployeePickerModal mode={picker} onClose={() => setPicker(null)} /> : null}

      <div className="flex gap-1 text-xs">
        {(Object.keys(FILTERS) as FilterKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={`rounded px-3 py-1 border ${filter === k ? 'bg-gray-900 text-white border-gray-700' : 'border-gray-800 text-gray-400 hover:bg-gray-950'}`}
          >
            {FILTERS[k].label}
          </button>
        ))}
      </div>

      {isLoading ? <p className="text-gray-500">Loading...</p> : null}
      {isError ? (
        <Alert variant="error">
          {error instanceof ApiError ? error.message : 'Could not load loans.'}
        </Alert>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? (
        <div className="rounded border border-dashed border-gray-800 p-10 text-center text-gray-400">No loans in this view.</div>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-500 uppercase text-[0.65rem] border-b border-gray-900">
              <tr>
                <th className="text-left py-2 font-medium">Employee</th>
                <th className="text-left py-2 font-medium">Status</th>
                <th className="text-right py-2 font-medium">Principal</th>
                <th className="text-right py-2 font-medium">Monthly</th>
                <th className="text-right py-2 font-medium">Repaid</th>
                <th className="text-right py-2 font-medium">Remaining</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <LoanRow key={a.id} loan={a} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function LoanRow({ loan }: { loan: AdvanceListRow }): JSX.Element {
  const ccy = loan.currency as CurrencyCode;
  const principal = Number(loan.principalAmount);
  const remaining = Number(loan.remainingBalance);
  const repaid = Math.max(0, principal - remaining);
  const pct = principal > 0 ? Math.min(100, Math.round((repaid / principal) * 100)) : 0;
  const name = loan.employee ? `${loan.employee.firstName} ${loan.employee.lastName}` : loan.employeeId;

  return (
    <tr className="border-b border-gray-900/60">
      <td className="py-2">
        <div className="text-white">{name}</div>
        <div className="text-xs text-gray-500">
          {loan.employee?.employeeNumber ?? '—'} · {loan.kind.replace('_', ' ')} · since {formatDate(loan.startPeriod)}
        </div>
      </td>
      <td className="py-2">
        <span className="text-xs rounded px-2 py-0.5 border border-gray-700 text-gray-300">{loan.status}</span>
      </td>
      <td className="py-2 text-right text-xs text-gray-300">{formatMoney(loan.principalAmount, ccy)}</td>
      <td className="py-2 text-right text-xs text-gray-300">{formatMoney(loan.monthlyDeduction, ccy)}</td>
      <td className="py-2 text-right">
        <div className="text-xs text-gray-300">{formatMoney(repaid.toFixed(4), ccy)}</div>
        <div className="mt-1 h-1 w-20 ml-auto rounded bg-gray-800 overflow-hidden">
          <div className="h-full bg-gray-400" style={{ width: `${pct}%` }} />
        </div>
      </td>
      <td className="py-2 text-right text-xs text-white">{formatMoney(loan.remainingBalance, ccy)}</td>
      <td className="py-2 text-right">
        {loan.employee ? (
          <Link to="/employees/$id/advances" params={{ id: loan.employee.id }} search={{ mode: 'new' as const }} className="text-xs text-gray-500 hover:underline whitespace-nowrap">
            Detail
          </Link>
        ) : null}
      </td>
    </tr>
  );
}
